import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, Shield, Users, MapPin, Briefcase, Calendar, ArrowLeft, Search, ExternalLink
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfessionalProfilePage() {
  const { proId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (proId) fetchProfile(proId);
  }, [proId]);

  const fetchProfile = async (id) => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await axios.get(`${API}/ca/profile/${id}`);
      setProfile(res.data);
    } catch {
      setNotFound(true);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 3) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/ca/profile-search`, { params: { q: searchQuery.trim() } });
      setSearchResults(res.data.results || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-12 px-4" data-testid="pro-profile-not-found">
        <div className="max-w-lg mx-auto text-center">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-slate-400" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Professional Not Found</h1>
          <p className="text-sm text-slate-500 mb-6">
            The professional ID <span className="font-mono font-semibold text-slate-700">{proId}</span> could not be found.
          </p>

          {/* Search Bar */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm font-medium text-slate-700 mb-3">Find an Accountant</p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Search by ID or name..."
                  className="pl-9 text-sm"
                  data-testid="pro-search-input"
                />
              </div>
              <Button onClick={handleSearch} disabled={searching} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm" data-testid="pro-search-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
              </Button>
            </div>
            {searchResults.length > 0 && (
              <div className="mt-3 divide-y divide-slate-100 border border-slate-100 rounded-lg">
                {searchResults.map((r) => (
                  <Link
                    key={r.professional_id}
                    to={`/pro/${r.professional_id}`}
                    className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors"
                    data-testid={`search-result-${r.professional_id}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-sm">
                      {r.name?.charAt(0) || "?"}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.professional_id}{r.firm_name ? ` | ${r.firm_name}` : ""}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mt-6" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-8 px-4" data-testid="pro-profile-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
          data-testid="pro-profile-card"
        >
          {/* Header Banner */}
          <div className="h-28 bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] relative">
            <div className="absolute -bottom-10 left-6">
              <div className="w-20 h-20 rounded-2xl bg-white border-4 border-white shadow-lg flex items-center justify-center text-[#7C3AED] font-bold text-2xl">
                {profile.name?.charAt(0) || "?"}
              </div>
            </div>
          </div>

          {/* Profile Info */}
          <div className="pt-14 px-6 pb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-xl font-bold text-slate-900" data-testid="pro-name">{profile.name}</h1>
                {profile.firm_name && (
                  <p className="text-sm text-slate-600 mt-0.5" data-testid="pro-firm">{profile.firm_name}</p>
                )}
              </div>
              <span className="text-xs font-mono font-semibold bg-violet-50 text-violet-700 px-3 py-1.5 rounded-lg border border-violet-100" data-testid="pro-id-badge">
                {profile.professional_id}
              </span>
            </div>

            {profile.bio && (
              <p className="text-sm text-slate-600 leading-relaxed mb-5" data-testid="pro-bio">{profile.bio}</p>
            )}

            <div className="grid grid-cols-2 gap-3 mb-5">
              {profile.specialization && (
                <div className="flex items-center gap-2.5 p-3 bg-slate-50 rounded-xl">
                  <Briefcase className="w-4 h-4 text-violet-500 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Specialization</p>
                    <p className="text-sm font-medium text-slate-800" data-testid="pro-spec">{profile.specialization}</p>
                  </div>
                </div>
              )}
              {profile.location && (
                <div className="flex items-center gap-2.5 p-3 bg-slate-50 rounded-xl">
                  <MapPin className="w-4 h-4 text-violet-500 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Location</p>
                    <p className="text-sm font-medium text-slate-800" data-testid="pro-location">{profile.location}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2.5 p-3 bg-slate-50 rounded-xl">
                <Users className="w-4 h-4 text-violet-500 flex-shrink-0" />
                <div>
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Clients Managed</p>
                  <p className="text-sm font-bold text-slate-800" data-testid="pro-client-count">{profile.clients_managed}</p>
                </div>
              </div>
              {profile.active_since && (
                <div className="flex items-center gap-2.5 p-3 bg-slate-50 rounded-xl">
                  <Calendar className="w-4 h-4 text-violet-500 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Active Since</p>
                    <p className="text-sm font-medium text-slate-800" data-testid="pro-since">{profile.active_since}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
              <Shield className="w-4 h-4 text-emerald-500" />
              <p className="text-xs text-slate-500">InvoiceSetu Professional</p>
            </div>
          </div>
        </motion.div>

        {/* Search Other Professionals */}
        <div className="mt-6 bg-white rounded-xl border border-slate-100 shadow-sm p-5" data-testid="pro-search-section">
          <p className="text-sm font-semibold text-slate-700 mb-3">Find Another Accountant</p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Search by Professional ID or name..."
                className="pl-9 text-sm"
                data-testid="pro-search-input-bottom"
              />
            </div>
            <Button onClick={handleSearch} disabled={searching} variant="outline" className="text-sm" data-testid="pro-search-btn-bottom">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          {searchResults.length > 0 && (
            <div className="mt-3 divide-y divide-slate-100 border border-slate-100 rounded-lg">
              {searchResults.map((r) => (
                <Link
                  key={r.professional_id}
                  to={`/pro/${r.professional_id}`}
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors"
                  data-testid={`search-result-bottom-${r.professional_id}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-sm">
                    {r.name?.charAt(0) || "?"}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-500">{r.professional_id}{r.firm_name ? ` | ${r.firm_name}` : ""}</p>
                  </div>
                  <span className="text-xs text-slate-400">{r.clients_managed} clients</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
